import { ServerHelper } from "@/api/ServerHelper";
import { Config } from "@/settings/Config";
import { TopicIndexDto } from "@/models/dto/TopicIndexDto";
import { AdditionalFiltersDto } from "@/models/dto/AdditionalFiltersDto";
import { VariantFilter } from "@/models/VariantFilter";
import { CategoryModel } from "@/models/CategoryModel";
import { PrintObject } from "@/models/PrintObject";
import { PrintBasket } from "@/models/PrintBasket";
import { TopicModel } from "@/models/TopicModel";
import {ReleaseNotesDto} from "@/models/dto/ReleaseNotesDto";
import {AllValidityDomainsDto} from "@/settings/ValidityDomains";

export class Api {
  private static _config = new Config();

  public static async loadMarkets(): Promise<string[]> {
    return await Api.get(`${ServerHelper.getBaseApiUrl()}/api/filter/getmarkets?v=${this._config.version}`);
  }

  public static async loadModels(market: string): Promise<string[]> {
    return await Api.get(`${ServerHelper.getBaseApiUrl()}/api/filter/getmodels?market=${market}&v=${this._config.version}`);
  }

  public static async loadStructureWeeks(market: string, model: string): Promise<string[]> {
    return await Api.get(`${ServerHelper.getBaseApiUrl()}/api/filter/getstructureweeks?market=${market}&model=${model}&v=${this._config.version}`);
  }

  public static async loadAdditionalFilters(
    market: string,
    model: string,
    structureWeek: string
  ): Promise<AdditionalFiltersDto> {
    const result = await Api.get(
      `${ServerHelper.getBaseApiUrl()}/api/filter/getadditionaldata?market=${market}&model=${model}&structureweek=${structureWeek}&v=${this._config.version}`
    );

    return result as AdditionalFiltersDto;
  }

  public static async loadTopic(id: string | number, locale: string, platform: string, file: string = ""): Promise<string> {
    let result: string;
    
    if (this._config.useNewContentNodes) {
        const url = `${ServerHelper.getBaseUrl()}/api/content/topic-html/${id}?lang=${locale}`;
        result = await Api.get(url);
    }
    else {
      const localeFolder = ServerHelper.getLocaleDataPath(platform, locale);
      let filePath = file ? file : id;
      filePath = `${ServerHelper.getBaseUrl()}/${localeFolder}/${this._config.htmlDirname}/${filePath}.html?v=${
          this._config.version
      }`;

      result = await Api.get(filePath);
    }
    
    return result as string;
  }

  public static async loadTopicIndex(locale: string, platform: string): Promise<TopicModel[]> {    
    if (this._config.useNewContentNodes) {
      const url = `${ServerHelper.getBaseUrl()}/api/content/topics?lang=${locale}&platform=${platform}`;
      const result = await Api.get(url);
      return result as TopicModel[];
    }
    else {
      const url = `${ServerHelper.getBaseUrl()}/${ServerHelper.getLocaleDataPath(platform, locale)}/${
          this._config.topicsFilename
      }?v=${this._config.version}`;

      const result = await Api.get(url);
      
      const topics: TopicModel[] = [];
      // Create a list of topics including the latest versions
      (result as TopicIndexDto[]).forEach((t) => {
        if (t.versions && t.versions.length > 0) {
          topics.push(t.versions[t.versions.length - 1]);
        }
      });
      
      return topics;
    }
  }

  public static async loadCategoryIndex(locale: string, platform: string): Promise<CategoryModel[]> {
    let url: string;
    
    if (this._config.useNewContentNodes) {
      url = `${ServerHelper.getBaseUrl()}/api/content/categories?lang=${locale}&platform=${platform}`;
    }
    else {
      url = `${ServerHelper.getBaseUrl()}/${ServerHelper.getLocaleDataPath(platform, locale)}/${
          this._config.nodesFilename
      }?v=${this._config.version}`;
    }

    const result = await Api.get(url);
    return result as CategoryModel[];
  }

  public static async loadValidities(): Promise<AllValidityDomainsDto> {
    const url = `${ServerHelper.getBaseUrl()}/api/filter/getuniquefilters`;
    const result = await Api.get(url);
    
    return result as AllValidityDomainsDto;
  }
  
  public static async loadReleaseNotes(locale: string): Promise<TopicModel | undefined> {
    if (this._config.useNewContentNodes) {
        const url = `${ServerHelper.getBaseUrl()}/api/content/release-notes?lang=${locale}`;
        const result = await Api.get(url);
        return result as TopicModel;
    }
    else {
       const url = `${ServerHelper.getBaseUrl()}/${ServerHelper.getPortalPath()}/${this._config.releaseNotesFilename}?v=${this._config.version}`;

      const result = (await Api.get(url) as ReleaseNotesDto);
      return result ? result[locale] : undefined;
    }
  }

  public static async setReleaseNotesUnsubscribe(unsubscribe: boolean) {
    const path = `${ServerHelper.getBaseApiUrl()}/api/umbmember/setreleasenotesunsubscribe?unsubscribe=${unsubscribe}`;
    await Api.post(path);
  }

  public static async sendFeedback(
    url: string,
    // formData: FormData,
    formData: any,
    filters: VariantFilter<string | string[]>[]
  ): Promise<boolean> {
    const data = {
      url: url,
    }

    Object.assign(data, formData);
    
    filters.forEach((filter) => {
      // if (Array.isArray(filter.entry)) {
      //   data[filter.domain] = JSON.stringify(filter.entry);
      // } else {
        data[filter.domain] = filter.entry;
      // }
    });

    const result = await Api.post(`${ServerHelper.getBaseApiUrl()}/api/feedback/submit`, data);

    return result.success;
  }

  public static async loadPrintBasket(): Promise<PrintBasket> {
    const result = await Api.get(`${ServerHelper.getBaseApiUrl()}/api/printbasket/getprintbasket`);
    return result as PrintBasket;
  }

  public static async uploadPrintBasket(printObjects: PrintObject[]): Promise<PrintBasket> {
    const result = await Api.put(`${ServerHelper.getBaseApiUrl()}/api/printbasket/updatebasket`, printObjects);

    return result as PrintBasket;
  }

  public static async addToPrintBasket(printObjects: PrintObject[]): Promise<PrintBasket> {
    const result = await Api.post(
      `${ServerHelper.getBaseApiUrl()}/api/printbasket/additems`,
      printObjects
    );

    return result as PrintBasket;
  }

  public static async removeFromPrintBasket(printObjects: PrintObject[]): Promise<PrintBasket> {
    const result = await Api.delete(`${ServerHelper.getBaseApiUrl()}/api/printbasket/removeitems`, printObjects);
    return result as PrintBasket;
  }

  public static async generatePDF(
    topicCount: number,
    documentParts: any[],
    filename: string,
    locale: string
  ): Promise<string> {
    const result = await Api.post(`${ServerHelper.getBaseApiUrl()}/api/documentgenerate/generate`, {
      documentParts: documentParts,
      filename: filename,
      filetype: "pdf",
      topicCount: topicCount,
      language: locale,
    }, false);
    
    return result as string;
  }

  public static async generateDocx(
    topicCount: number,
    documentParts: any[],
    filename: string,
    locale: string
  ): Promise<string> {
    const result = await Api.post(`${ServerHelper.getBaseApiUrl()}/api/documentgenerate/generate`, {
      documentParts: documentParts,
      filename: filename,
      filetype: "docx",
      topicCount: topicCount,
      language: locale,
    }, false);

    return result as string;
  }

  public static async trackSearch(
    keyword: string,
    hitCount: number,
    trackUrl: string,
    lang: string,
    sessionId: string,
    referrer: string | undefined = undefined
  ) {
    const data = {
      url: trackUrl,
      language: lang,
      search: keyword,
      hitCount: hitCount,
      referrer: referrer,
      sessionId: sessionId,
    };
    const url = `${ServerHelper.getBaseApiUrl()}/api/event/search`;

    await Api.post(url, data, false);
  }

  public static async trackAddedToBasket(
    pageName: string,
    trackUrl: string,
    lang: string,
    category: CategoryModel,
    subCategory: CategoryModel,
    sessionId: string,
    referrer: string | undefined = undefined
  ) {
    const data = {
      url: trackUrl,
      language: lang,
      pageName: pageName,
      categoryId: category ? category.id.toString() : null,
      categoryTitle: category ? category.title : null,
      subCategoryId: subCategory ? subCategory.id.toString() : null,
      subCategoryTitle: subCategory ? subCategory.title : null,
      referrer: referrer,
      sessionId: sessionId,
    };

    const url = `${ServerHelper.getBaseApiUrl()}/api/event/addtobasket`;

    await Api.post(url, data, false);
  }

  public static async trackPageView(
    topic: TopicModel,
    trackUrl: string,
    lang: string,
    duration: number,
    progress: number,
    referrer: string,
    category: CategoryModel,
    subCategory: CategoryModel,
    sessionId: string
  ) {
    const data = {
      topicId: topic.id.toString(),
      url: trackUrl,
      language: lang,
      topicTitle: topic.title,
      duration: duration,
      progress: progress,
      referrer: referrer,
      sessionId: sessionId,
      categoryId: category ? category.id.toString() : null,
      categoryTitle: category ? category.title : null,
      subCategoryId: subCategory ? subCategory.id.toString() : null,
      subCategoryTitle: subCategory ? subCategory.title : null,
    };

    const url = `${ServerHelper.getBaseApiUrl()}/api/event/pageview`;

    await Api.post(url, data, false);
  }

  private static async get(url: string) {
    return await Api.send(url, "get");
  }
  
  private static async post(url: string, data: any = undefined, jsonResponse: boolean = true) {
    return await Api.send(url, "post", data, jsonResponse)
  }
  
  private static async put(url: string, data: any) {
    return await Api.send(url, "put", data);
  }
  
  private static async delete(url: string, data: any = undefined) {
    return await Api.send(url, "delete", data)
  }
  
  private static async send(url: string, type: string, data: any = undefined, jsonResponse: boolean = true) {
    const settings = {
      url: url,
      type: type,
      contentType: "application/json",
      processData: false,
    } as any;

    if (data) {
      settings.data = JSON.stringify(data);
    }
    
    if (data && jsonResponse) {
      settings.dataType = "json";
    }
    
    try {
      return await $.ajax(settings);
    }
    catch(e) {
      if (e && e.status && (e.status === 401 || e.status === 403)) {
        location.reload(); 
      }
      else {
        throw e;
      }
    }
  }
}
